import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PaymentsService } from '../../../core/services/payments.service';
import { ToastMessageService } from '../../../core/services/toast-message.service';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-payment-redirect',
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule],
  template: `
    <div class="payment-result-container">
      <div class="result-card">
        <div class="icon-container" [ngClass]="{'success': paymentSuccess, 'error': paymentFailed}">
          <i class="pi" [ngClass]="{'pi-check': paymentSuccess, 'pi-times': paymentFailed, 'pi-spin pi-spinner': isVerifying}"></i>
        </div>
        <h2>{{resultTitle}}</h2>
        <p>{{resultMessage}}</p>
        <div class="countdown" *ngIf="paymentVerified">
          <span>Closing window in {{countdown}} seconds...</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .payment-result-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background-color: #f5f5f5;
      font-family: Arial, sans-serif;
    }

    .result-card {
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      padding: 30px;
      text-align: center;
      max-width: 500px;
      width: 90%;
    }

    .icon-container {
      font-size: 48px;
      margin-bottom: 20px;
      height: 80px;
      width: 80px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
    }

    .icon-container.success {
      background-color: #d4edda;
      color: #28a745;
    }

    .icon-container.error {
      background-color: #f8d7da;
      color: #dc3545;
    }

    h2 {
      margin-bottom: 15px;
      color: #333;
    }

    p {
      color: #666;
      margin-bottom: 20px;
    }

    .countdown {
      font-size: 14px;
      color: #777;
      margin-top: 30px;
    }
  `]
})
export class PaymentRedirectComponent implements OnInit {
  isVerifying: boolean = true;
  paymentSuccess: boolean = false;
  paymentFailed: boolean = false;
  paymentVerified: boolean = false;
  resultTitle: string = 'Verifying Payment...';
  resultMessage: string = 'Please wait while we verify your payment.';
  countdown: number = 5;

  constructor(
    private route: ActivatedRoute,
    private paymentsService: PaymentsService,
    private toastMessage: ToastMessageService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['type'] === 'redirect_after_finish_payment' && params['payment_uuid']) {
        this.verifyPayment(params['payment_uuid']);
      } else {
        this.showFailedResult('Invalid payment parameters');
      }
    });
  }

  verifyPayment(paymentUuid: string) {
    this.paymentsService.verifyPayment(paymentUuid).subscribe({
      next: (response: any) => {
        if (response.data.payment_status === 'CAPTURED') {
          this.showSuccessResult();
        } else {
          this.showFailedResult('Payment was not successful. Please try again.');
        }
      },
      error: (error) => {
        this.showFailedResult('Failed to verify payment.');
      }
    });
  }

  showSuccessResult() {
    this.isVerifying = false;
    this.paymentSuccess = true;
    this.paymentVerified = true;
    this.resultTitle = 'Payment Successful!';
    this.resultMessage = 'Your payment has been processed successfully.';
    this.startCountdown();

    // Notify the opener window about success
    if (window.opener) {
      window.opener.postMessage({type: 'payment_verified', success: true}, '*');
    }
  }

  showFailedResult(message: string) {
    this.isVerifying = false;
    this.paymentFailed = true;
    this.paymentVerified = true;
    this.resultTitle = 'Payment Failed';
    this.resultMessage = message;
    this.startCountdown();

    // Notify the opener window about failure
    if (window.opener) {
      window.opener.postMessage({type: 'payment_verified', success: false}, '*');
    }
  }

  startCountdown() {
    const timer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(timer);
        window.close();
      }
    }, 1000);
  }
}
